import { turnAppliedChanges } from './agentInteractionState';
import type { AgentChatEntry, AgentPublishedDraftResult, AgentTurn } from './types';

type DraftResult = Extract<AgentChatEntry, { role: 'draft-result' }>;

/** Historical outputs never borrow the current draft's name or another entity's application state. */
export const buildResultRail = (entries: AgentChatEntry[]) => {
    const publications = new Map<number, AgentPublishedDraftResult>();
    const turnById = new Map<number, AgentTurn>();
    const seen = new Set<string>();
    const resultOutputs: DraftResult[] = [];
    const shownDraftIds = new Set<number>();
    for (const entry of entries) {
        if (entry.role === 'agent') {
            for (const item of entry.turn.publishedDrafts ?? []) publications.set(item.draftId, item);
            if (entry.turn.turnId != null) turnById.set(entry.turn.turnId, entry.turn);
        }
        if (entry.role !== 'draft-result' || entry.draftId == null) continue;
        const key = `${entry.draftId}:${entry.resultNumber}`;
        if (seen.has(key)) continue;
        seen.add(key);
        // Applying an approved output (or only its assignments) re-publishes the same entity as a new revision. That
        // revision is not a new proposal to look at: the output already on screen is the one that was approved, so it
        // keeps the stage and takes the created state, instead of a second card appearing under the confirmation.
        const turn = turnById.get(entry.turnId);
        if (turn && turnAppliedChanges(turn) && shownDraftIds.has(entry.draftId)) continue;
        shownDraftIds.add(entry.draftId);
        resultOutputs.push(entry);
    }
    const statuses = new Map<string, 'draft' | 'created' | 'unused'>();
    const latestAppliedResults = new Map<number, DraftResult>();
    for (const result of resultOutputs) {
        const appliedRevision = publications.get(result.draftId!)?.appliedRevisionNumber;
        if (appliedRevision != null && result.revisionNumber != null && result.revisionNumber <= appliedRevision) {
            latestAppliedResults.set(result.draftId!, result);
        }
    }
    for (const result of resultOutputs) {
        const publication = publications.get(result.draftId!);
        let status: 'draft' | 'created' | 'unused' = 'draft';
        if (publication?.appliedRevisionNumber != null && result.revisionNumber != null && result.revisionNumber <= publication.appliedRevisionNumber) {
            status = 'created';
            if (latestAppliedResults.get(result.draftId!)?.id !== result.id) status = 'unused';
        }
        statuses.set(result.id, status);
    }
    return { resultOutputs, statuses };
};
